// @flow
import React, { useMemo } from 'react';
import { get, isNil, size } from 'lodash';
import { SkeletonRows, FormattedText, Table } from '@elements';
import type { ReceiptsState } from '@types';
import { sort, useTranslationNs, fsPrintf } from '@helpers/helpers';
import FinancialItemCard from './FinancialItemCard';

type Props = {
    receipts: ReceiptsState,
    maxListItens: number,
    handleShowReceiptClick: Function,
    singleDoctor: boolean,
};

const PatientFinancialReceipts = ({
  receipts, maxListItens, handleShowReceiptClick,
  singleDoctor,
}: Props) => {
  const [_] = useTranslationNs('financial');

  const receiptsData = sort(get(receipts, ['data'], {}), (a, b) => b.id - a.id);

  const onItemClick = (receipt) => handleShowReceiptClick(receipt.id)();


  const columns = useMemo(() => [
    {
      dataIndex: 'number',
      content: _('label/number', 'global'),
    },
    {
      dataIndex: 'date',
      content: _('label/date', 'global'),
      onRenderItem: (receipt) => <FormattedText type="date" text={receipt.date} />,
    },
    {
      dataIndex: 'doctorName',
      content: _('label/doctor', 'global'),
      hidden: singleDoctor,
    },
    {
      dataIndex: 'price',
      content: _('label/value', 'global'),
      onRenderItem: (receipt) => <FormattedText type="currency" text={receipt.price} />,
    },
  ], [_, singleDoctor]);

  const renderMobileItem = (receipt, { onClick }) => (
    <FinancialItemCard
      title={isNil(receipt.number) || receipt.number === '' ? _('label/receipt') : fsPrintf(receipt.number)(_('label/receipt_number'))}
      value={<FormattedText type="currency" text={receipt.price} />}
      details={receipt.notes}
      date={receipt.date}
      doctorName={singleDoctor ? null : receipt.doctorName}
      onClick={onClick}
    />
  );

  if (get(receipts, 'isLoading') && size(get(receipts, 'data', [])) === 0) {
    return <SkeletonRows />;
  }

  return (
    <Table
      enablePagination
      itemKey="id"
      limit={maxListItens}
      columns={columns}
      onItemClick={onItemClick}
      items={receiptsData}
      renderMobileItem={renderMobileItem}
      emptyMessage={_('label/receipts_empty/title')}
    />
  );
};

// $FlowFixMe
export default React.memo(PatientFinancialReceipts);
